/**
 * Web component: Geolocation popup (geolocation-popup)
 * -----------------------------------------------------------------------------
 * Geolocation modal with suggested redirect.
 * - Component settings: section / dynamic / web / vanilla
 *
 */
import './geolocation-popup.scss';

import { vueReady } from '~/core/runners';
import { markComponentActive } from '~/helpers/web-components';
import useIndexStore from '~stores/index';
import useOverlaysStore from '~stores/overlays';

import metaobjectQuery from '~graphql/geolocation.gql';
import countries from '~/helpers/countries';
import { transformFieldsToObject } from '~/format/format-content-metaobject';
import { convertSchemaToHtml } from '@thebeyondgroup/shopify-rich-text-renderer';

/**
 * DOM selectors.
 */
const selectors = {
  formCountry: 'input[name="country_code"]',
  formLanguage: 'input[name="language_code"]',
  ctaButtonLabel: '.localization-form__buttons .submit span',
  ctaTextLink: '.localization-form__buttons .modal-close a',
  titleFlag: '.geolocation-popup__title-flag',
  title: '.geolocation-popup__title-text',
  body: '.geolocation-popup__text',
};

/**
 * Create new <geolocation-popup> custom element.
 */
class GeolocationPopup extends HTMLElement {
  data(component) {
    // add all languages where the ISO code differes from the country code
    const mapLanguageToCountry = [];
    mapLanguageToCountry.EN = 'GB';

    return {
      indexStore: useIndexStore(),
      overlaysStore: useOverlaysStore(),
      mapLanguageToCountry,
      overlayNamespace: 'geolocationPopup',
      selectors: {
        formCountry: component.querySelector(selectors.formCountry),
        formLanguage: component.querySelector(selectors.formLanguage),
        ctaButtonLabel: component.querySelector(selectors.ctaButtonLabel),
        ctaTextLink: component.querySelector(selectors.ctaTextLink),
        titleFlag: component.querySelector(selectors.titleFlag),
        title: component.querySelector(selectors.title),
        body: component.querySelector(selectors.body),
      },
    };
  }

  /**
   * Custom element constructor.
   * - Always call super first in constructor.
   */
  constructor() {
    super();
    this.$data = this.data(this);
  }

  /**
   * Lifecycle hook - component is attached to DOM.
   */
  async connectedCallback() {
    markComponentActive(this);

    const overlay = {
      namespace: this.$data.overlayNamespace,
    };

    if (this.$data.overlaysStore.getOverlayDismissed(overlay)) {
      return;
    }

    const { country, language } = this.$data.indexStore.getGeolocationCountry
      .code
      ? {
          country: this.$data.indexStore.getGeolocationCountry,
          language: this.$data.indexStore.getGeolocationLanguage,
        }
      : await this.getSuggestions();

    if (
      country &&
      country.code !== 'None' &&
      country.code !== cnvs.store.country
    ) {
      this.$data.selectors.ctaTextLink?.addEventListener('click', () => {
        this.emitOverlayEvent('close');
      });

      if (language !== cnvs.store.language) {
        await this.translateContent(country, language);
      }

      this.$data.selectors.formCountry.value = country.code;
      this.$data.selectors.formLanguage.value = language;

      const flag = this.$data.selectors.titleFlag?.src;
      if (flag) {
        const regex = /\/[^/]*\.svg/;
        this.$data.selectors.titleFlag.src = flag.replace(
          regex,
          `/${country.code.toLowerCase()}.svg`,
        );
      }

      this.emitOverlayEvent('open');
    }
  }

  /**
   * Query Shopify Browsing Context Suggestions API and return country and language:
   * https://shopify.dev/docs/storefronts/themes/markets/localization-discovery#step-1-query-for-browsing-context-suggestions
   * @returns {Object} - Object with country and language handle strings
   */
  getSuggestions() {
    return fetch(
      `${window.Shopify.routes.root}browsing_context_suggestions.json` +
        `?country[enabled]=true` +
        `&language[enabled]=true`,
    )
      .then((resp) => resp.json())
      .then((result) => {
        const country = {
          code: result.suggestions[0]?.parts.country?.handle
            ? result.suggestions[0].parts.country.handle.toUpperCase()
            : 'None',
          name: result.suggestions[0]?.parts.country?.name
            ? result.suggestions[0].parts.country.name
            : 'None',
        };

        const language = result.suggestions[0]?.parts.language?.handle
          ? result.suggestions[0].parts.language.handle.toUpperCase()
          : 'EN';

        // if english, always use internal country name
        if (language === 'EN' && country.code !== 'None') {
          country.name = countries[country.code];
        }

        // update stored data on every call
        this.$data.indexStore.setGeolocationCountry(country);
        this.$data.indexStore.setGeolocationLanguage(language);

        return { country, language };
      });
  }

  /**
   * API limitation: available languages are tied to country according to
   * market settings. To force a language, send corresponding country in
   * context GQL query.
   */
  async translateContent(country, language) {
    const countryMock = Object.hasOwn(this.$data.mapLanguageToCountry, language)
      ? this.$data.mapLanguageToCountry[language]
      : language;

    const response = await cnvs.Query({
      query: metaobjectQuery,
      variables: {
        handle: {
          handle: this.dataset.handle,
          type: 'geolocation_popup',
        },
        country: countryMock,
        language,
      },
    });

    if (!response?.metaobject) {
      return;
    }

    const metaobject = transformFieldsToObject(response.metaobject);
    const { title, body, ctaButtonLabel, ctaTextLink } = this.$data.selectors;

    title.textContent = metaobject.title.replace('{{ country }}', country.name);
    body.innerHTML = convertSchemaToHtml(metaobject.body);
    ctaButtonLabel.textContent = metaobject.cta_button_label.replace(
      '{{ country }}',
      country.name,
    );
    ctaTextLink.textContent = metaobject.cta_text;
  }

  emitOverlayEvent(event) {
    if (event && event !== '') {
      cnvs.EventBus.emit(`overlay:${event}`, {
        namespace: this.$data.overlayNamespace,
        queue: event === 'open',
      });
    }
  }
}

/**
 * Define web component on Vue initialisation.
 */
vueReady(() => {
  if (customElements.get('geolocation-popup')) {
    return;
  }

  customElements.define('geolocation-popup', GeolocationPopup);
});
